
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '@mdi/react';
import * as XLSX from 'xlsx';
import { mdiClose, mdiTableArrowDown } from '@mdi/js';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { forEach, includes, map } from 'lodash';
import toast from 'react-hot-toast';

import RefinancingModalErrorComponent from './RefinancingModalErrorComponent';

const ignoreAccentuations = (text) => {
  if (text) return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  else return '';
}
const requiredHeaders = ['nombre', 'apellido paterno', 'apellido materno', 'rut', 'teléfono', 'mail'];
let dragCounter = 0;

const RefinancingModal = (props) => {
  const { modal, toggle, handleSubmit, manyCases, resetRefinancingCases } = props;

  const fileInputRef = useRef(null);
  const [fileData, setFileData] = useState(null);
  const [dragEvent, setDragEvent] = useState(null);

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    setFileData(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    setFileData(file);
    e.target.classList.remove("drag-over");
    e.target.classList.add("drop-complete");
    setDragEvent(e);
    dragCounter = 0;
  };

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation();
    e.target.classList.add("drag-over");
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.classList.add("drag-over");
    dragCounter++;
  }

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter--;
    if (dragCounter === 0) e.target.classList.remove("drag-over");
  }

  const handleInputClick = () => {
    if (fileInputRef.current) {
      const newInput = document.createElement('input');
      newInput.type = 'file';
      newInput.accept = '.xlsx, .xls';
      newInput.addEventListener('change', handleFileInputChange);
      newInput.click();
      if (fileInputRef.current.parentNode) {
        fileInputRef.current.parentNode.removeChild(fileInputRef.current);
      }
      fileInputRef.current = newInput;
    }
  };

  const handleFormatData = (excelData) => {
    if (excelData.length) {
      const headers = excelData[0];
      let invalidHeaders = [];
      const normalizedHeaders = map(headers, header => ignoreAccentuations(header).trim());
      forEach(requiredHeaders, (header, index) => {
        let result = includes(normalizedHeaders, ignoreAccentuations(header));
        if (!result) invalidHeaders.push({ correct: header, found: normalizedHeaders[index] });
      });
      if (invalidHeaders.length) {
        toast.error(
          <div className='crm-invalid-container'>
            <div className='crm-invalid-title'>
              {invalidHeaders.length === 1 ? 'Se encontró un encabezado incorrecto' : 'Se econtraron varios encabezados incorrectos'}
            </div>
            <div className='crm-table-container'>
              <table>
                <thead><tr><th>Correcto</th><th>Encontrado</th></tr></thead>
                <tbody>
                  {map(invalidHeaders, e => <tr><td>{e.correct}</td><td>{e.found}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        );
        return;
      }

      const formattedExcel = map(excelData.slice(1), (e) => {
        let objectValues = {};
        forEach(headers, (header, index) => objectValues[header.trim()] = e[index]);
        return objectValues;
      });
      handleSubmit(formattedExcel);
    }
  }

  const handleFileUpload = (event) => {
    event.preventDefault();
    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: 'binary' });
      const sheetName = workbook.SheetNames[0]; // Assuming data is in the first sheet
      const sheet = workbook.Sheets[sheetName];
      const excelData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      handleFormatData(excelData);
    };
    reader.readAsBinaryString(fileData);
  };

  const handleCleanInput = () => {
    setFileData(null);
    if (dragEvent?.target) dragEvent.target.classList.remove("drop-complete");
  }

  const handleCancel = () => {
    resetRefinancingCases();
    handleCleanInput();
    toggle();
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // Previene el comportamiento por defecto
      handleInputClick(); // Llama a tu función handleInputClick
    }
  };

  const handleProcessSubmit = (e) => {
    handleFileUpload(e);
    handleCleanInput();
  }

  return (
    <Modal className='crm-attach-modal-container' isOpen={modal} toggle={toggle} >
      <ModalHeader toggle={toggle}>Subir Excel de Refinanciamiento</ModalHeader>
      <ModalBody>
        {manyCases?.error?.length ? <RefinancingModalErrorComponent error={manyCases?.error || []} resetRefinancingCases={resetRefinancingCases} /> :
          <div className="crm-attach-container">
            <div className='crm-card-container'>
              <h1 className="crm-card-title">Adjuntar Archivo</h1>
              <hr className="crm-line" />
            </div>
            <form id='crm-attach-file' className="crm-attach-file-container" onSubmit={handleProcessSubmit} >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileInputChange}
              />
              {fileData?.name &&
                <button className='crm-attach-clean-btn' type="button" onClick={handleCleanInput}>
                  <Icon path={mdiClose} size={1} />
                </button>
              }
              <div
                className={`crm-attach-drop ${fileData?.name ? 'drop-complete' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onClick={handleInputClick}
                onKeyDown={handleKeyDown}
              >
                <div className='crm-empty-grop-container'>
                  {fileData?.name ?
                    <span className="crm-attached-text">{fileData?.name}</span> :
                    <>
                      <span className="crm-attach-empty-text" >Arrastrar o hacer click para adjuntar</span>
                      <Icon path={mdiTableArrowDown} />
                    </>
                  }
                </div>
              </div>

            </form>
          </div>
        }
      </ModalBody>
      <ModalFooter>
        <button className='crm-white-btn' onClick={handleCancel}>
          Cancelar
        </button>
        <button type='submit' className='crm-black-btn' form='crm-attach-file' disabled={!fileData?.name}>
          {manyCases.loading ? <Spinner size="sm" /> : 'Subir Archivo'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

RefinancingModal.propTypes = {
  modal: PropTypes.bool,
  toggle: PropTypes.func,
  handleSubmit: PropTypes.func,
  resetRefinancingCases: PropTypes.func,
  manyCases: PropTypes.shape({
    response: PropTypes.any,
    loading: PropTypes.bool,
    error: PropTypes.array
  })
};

export default RefinancingModal;